/**
 * `/settings/twilio` — Twilio BYOT (bring-your-own-Twilio) configuration.
 *
 * Tenants enter their own Account SID, Auth Token, and From Number.
 * Credentials are stored encrypted in the secrets service per-tenant.
 * The page also displays the webhook URLs to paste into the Twilio console.
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useTwilioSettings, useUpdateTwilioSettings } from "@/hooks/use-settings";

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={copy} className="shrink-0">
      {copied ? "Copied!" : "Copy"}
    </Button>
  );
}

export function SettingsTwilioPage() {
  const settings = useTwilioSettings();
  const update = useUpdateTwilioSettings();

  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [fromNumber, setFromNumber] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate({ account_sid: accountSid, auth_token: authToken, from_number: fromNumber });
  };

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Settings</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Twilio</h1>
        <p className="text-sm text-muted-foreground">
          Connect your Twilio account to enable SMS lead response and missed-call text-back.
        </p>
      </header>

      {/* Connection status */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Connection status</CardTitle>
            {settings.isLoading ? (
              <Skeleton className="h-5 w-20 rounded-full" />
            ) : (
              <Badge
                variant={settings.data?.configured ? "default" : "secondary"}
                className={settings.data?.configured ? "bg-green-100 text-green-800" : ""}
              >
                {settings.data?.configured ? "Connected" : "Not configured"}
              </Badge>
            )}
          </div>
        </CardHeader>
        {settings.data?.configured && settings.data.from_number && (
          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground">
              From number:{" "}
              <span className="font-mono font-medium text-gray-900">
                {settings.data.from_number}
              </span>
            </p>
          </CardContent>
        )}
      </Card>

      {/* Webhook URLs */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Webhook URLs</CardTitle>
          <p className="text-xs text-muted-foreground">
            Paste these into your Twilio phone number configuration.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {settings.isLoading ? (
            <>
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">
                  SMS webhook (Messaging → A message comes in)
                </Label>
                <div className="flex gap-2 items-center">
                  <Input
                    readOnly
                    value={settings.data?.webhook_sms_url ?? ""}
                    className="font-mono text-xs bg-gray-50"
                  />
                  <CopyButton value={settings.data?.webhook_sms_url ?? ""} />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">
                  Voice webhook (Voice → A call comes in)
                </Label>
                <div className="flex gap-2 items-center">
                  <Input
                    readOnly
                    value={settings.data?.webhook_voice_url ?? ""}
                    className="font-mono text-xs bg-gray-50"
                  />
                  <CopyButton value={settings.data?.webhook_voice_url ?? ""} />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Credentials form */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Credentials</CardTitle>
          <p className="text-xs text-muted-foreground">
            Find these in your{" "}
            <a
              href="https://console.twilio.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Twilio console
            </a>
            . Stored encrypted — not visible after saving.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="account-sid">Account SID</Label>
              <Input
                id="account-sid"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={accountSid}
                onChange={(e) => setAccountSid(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="auth-token">Auth Token</Label>
              <Input
                id="auth-token"
                type="password"
                placeholder="••••••••••••••••••••••••••••••••"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="from-number">From Number</Label>
              <Input
                id="from-number"
                placeholder="+15551234567"
                value={fromNumber}
                onChange={(e) => setFromNumber(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">E.164 format — must match a number in your Twilio account</p>
            </div>

            {update.isError && (
              <p className="text-sm text-destructive">
                {update.error instanceof Error ? update.error.message : "Failed to save credentials"}
              </p>
            )}
            {update.isSuccess && (
              <p className="text-sm text-green-700">Credentials saved successfully.</p>
            )}

            <Button
              type="submit"
              disabled={!accountSid || !authToken || !fromNumber || update.isPending}
              className="self-start"
            >
              {update.isPending ? "Saving…" : "Save credentials"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default SettingsTwilioPage;
